import { Badge } from '@/components/ui/badge';
import type { ReactElement, ReactNode } from 'react';
import { Status } from '@pillar-portal/database/dist/frontend';
import {
    CredentialTypeDisplayName,
    CredentialType,
    ThirdPartySystemId,
    formatThirdPartySystemName,
} from '@pillar-portal/api-types';

type CredentialCardDisplayData = {
    account_id: string;
    system_id: ThirdPartySystemId;
    credential_type: CredentialType;
    status: Status;
    applies_to_children: boolean;
    public_fields: Record<string, unknown>;
};

type CredentialCardViewProps = {
    credential: CredentialCardDisplayData;
    showPublicFields: boolean;
    actions?: ReactNode;
    children?: ReactNode;
    id?: string;
    className?: string;
    titleHref?: string;
};

type PublicFieldEntry = {
    key: string;
    value: string;
};

function getSystemDisplayName(systemId: ThirdPartySystemId): string {
    try {
        return formatThirdPartySystemName(systemId);
    } catch {
        return `System ${systemId}`;
    }
}

function getCredentialPublicFieldEntries(
    credential: CredentialCardDisplayData
): ReadonlyArray<PublicFieldEntry> {
    return Object.keys(credential.public_fields)
        .sort()
        .flatMap((fieldName): PublicFieldEntry[] => {
            const rawFieldValue: unknown = credential.public_fields[fieldName];
            if (rawFieldValue !== null && rawFieldValue !== undefined) {
                return [{ key: fieldName, value: String(rawFieldValue) }];
            }
            return [];
        });
}

export function CredentialCardView({
    credential,
    showPublicFields,
    actions,
    children,
    id,
    className,
    titleHref,
}: CredentialCardViewProps): ReactElement {
    return (
        <div
            id={id}
            className={`border rounded-lg p-4 transition-colors hover:bg-gray-50 ${className ?? ''}`.trim()}
        >
            <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h4 className="font-medium">
                            {titleHref === undefined ? (
                                credential.account_id
                            ) : (
                                <a className="text-primary underline" href={titleHref}>
                                    {credential.account_id}
                                </a>
                            )}
                        </h4>
                        <Badge variant="outline">{getSystemDisplayName(credential.system_id)}</Badge>
                        <Badge variant="secondary">
                            {CredentialTypeDisplayName[credential.credential_type]}
                        </Badge>
                        {credential.status !== Status.ACTIVE && (
                            <Badge variant="destructive">Inactive</Badge>
                        )}
                        {credential.applies_to_children && (
                            <Badge variant="secondary">Inherited by Children</Badge>
                        )}
                    </div>
                    {showPublicFields && (
                        <div className="text-sm text-muted-foreground space-y-1">
                            {getCredentialPublicFieldEntries(credential).map((entry) => (
                                <div key={entry.key}>
                                    <span className="font-medium">{entry.key}</span>: {entry.value}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {actions}
            </div>
            {children}
        </div>
    );
}
